import React from 'react';
import { listFind } from '@/function/list/list.find';
import { listFindSections } from '@/function/list/list.find.sections';
import { listSortDirection } from '@/function/list/list.sort.direction';
import { useListFilters } from '@/hooks/use.list.filters';
import type { TypeListFindBound, TypeListFindConfig, TypeListFindState } from '@/types/list/types.list.find';

const NONE: readonly never[] = [];

export function useListFind<T>(items: readonly T[], config: TypeListFindConfig<T> | null) {
    const filters = useListFilters(config?.sort ?? config?.sorts?.[0]?.value ?? '', config?.descending ?? true);
    const search = React.useDeferredValue(filters.search);
    const local = config?.local ?? true;

    const state: TypeListFindState = React.useMemo(() => ({
        search: local ? search : filters.search,
        active: filters.active,
        excluded: filters.excluded,
        dates: filters.dates,
        sort: filters.sort,
        descending: filters.descending,
    }), [local, search, filters.search, filters.active, filters.excluded, filters.dates, filters.sort, filters.descending]);

    const shown = React.useMemo(
        () => (config && local ? listFind(items, config, state) : (items as T[])),
        [config, local, items, state],
    );

    const sections = React.useMemo(
        () => (config ? listFindSections(items, config, state) : []),
        [items, config, state],
    );

    const { setSearch: writeSearch, toggle: flip, clear, setDate, setSort, toggleDirection, setExcluded, active } = filters;
    const searchMax = config?.searchMax;
    const setSearch = React.useCallback(
        (value: string) => writeSearch(searchMax ? value.slice(0, searchMax) : value),
        [writeSearch, searchMax],
    );
    const singles = React.useMemo(
        () => new Set((config?.facets ?? NONE).filter((facet) => facet.single).map((facet) => facet.id)),
        [config?.facets],
    );
    const toggle = React.useCallback((sectionId: string, value: string) => {
        const selected = active[sectionId] ?? [];
        if (singles.has(sectionId) && !selected.includes(value)) {
            for (const other of selected) flip(sectionId, other);
        }
        flip(sectionId, value);
    }, [active, flip, singles]);
    const reset = React.useCallback(() => { clear(); writeSearch(''); }, [clear, writeSearch]);

    const filtered = Boolean(filters.search.trim()) || filters.activeCount > 0;

    const find: TypeListFindBound | null = config ? {
        kind: 'bound',
        bar: {
            search: filters.search,
            onSearch: setSearch,
            placeholder: config.placeholder,
            sections,
            active: filters.active,
            onToggle: toggle,
            excluded: filters.excluded,
            onExclude: setExcluded,
            onClear: clear,
            onReset: reset,
            dates: (config.dates ?? NONE).map(({ id, label }) => ({ id, label })),
            dateValues: filters.dates,
            onDateChange: setDate,
            sort: config.sorts?.length && config.sorts.length > 1 ? {
                options: config.sorts.map((option) => ({ value: option.value, label: option.label, ...listSortDirection(option) })),
                value: filters.sort,
                onChange: setSort,
                descending: config.direction === false ? undefined : filters.descending,
                onToggleDirection: config.direction === false ? undefined : toggleDirection,
            } : undefined,
            filtered,
            shown: local ? shown.length : undefined,
            total: local ? items.length : undefined,
            right: config.right,
        },
    } : null;

    return { shown, find, state, filters: { ...filters, toggle, setSearch }, filtered, reset };
}

export default useListFind;
